import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarIcon, User, Activity, Clock, Filter } from "lucide-react";
import { format } from "date-fns";

interface AuditTrailEntry {
  id: number;
  entityType: string;
  entityId: string;
  action: string;
  fieldName?: string;
  oldValue?: string;
  newValue?: string;
  userId: string;
  userName?: string;
  timestamp: Date;
  ipAddress?: string;
  userAgent?: string;
}

interface AuditTrailProps {
  entityType?: string;
  entityId?: string;
  userId?: string;
  showFilters?: boolean;
}

const actionIcons = {
  create: <Activity className="w-4 h-4 text-green-500" />,
  update: <Activity className="w-4 h-4 text-blue-500" />,
  delete: <Activity className="w-4 h-4 text-red-500" />,
  restore: <Activity className="w-4 h-4 text-yellow-500" />,
};

const actionColors = {
  create: "bg-green-100 text-green-800",
  update: "bg-blue-100 text-blue-800",
  delete: "bg-red-100 text-red-800",
  restore: "bg-yellow-100 text-yellow-800",
};

export function AuditTrail({ entityType, entityId, userId, showFilters = true }: AuditTrailProps) {
  const [filters, setFilters] = useState({
    entityType: entityType || "all",
    entityId: entityId || "",
    userId: userId || "",
  });

  const [appliedFilters, setAppliedFilters] = useState(filters);

  const { data: auditEntries = [], isLoading, error } = useQuery({
    queryKey: ['/api/audit-trail', appliedFilters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (appliedFilters.entityType && appliedFilters.entityType !== "all") params.append('entityType', appliedFilters.entityType);
      if (appliedFilters.entityId) params.append('entityId', appliedFilters.entityId);
      if (appliedFilters.userId) params.append('userId', appliedFilters.userId);
      
      const url = `/api/audit-trail?${params.toString()}`;
      console.log('Audit Trail API Call:', url);
      
      const response = await fetch(url, {
        credentials: "include",
      });
      
      if (!response.ok) {
        const text = (await response.text()) || response.statusText;
        throw new Error(`${response.status}: ${text}`);
      }
      
      const data = await response.json();
      console.log('Audit Trail API Response:', data);
      
      return data as AuditTrailEntry[];
    },
  });

  const handleApplyFilters = () => {
    setAppliedFilters({ ...filters });
  };

  const handleClearFilters = () => {
    const clearedFilters = { entityType: "all", entityId: "", userId: "" };
    setFilters(clearedFilters);
    setAppliedFilters(clearedFilters);
  };

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="w-5 h-5" />
            Audit Trail
          </CardTitle>
          <CardDescription>Error loading audit trail</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Activity className="w-5 h-5" />
          Audit Trail
        </CardTitle>
        <CardDescription>
          View detailed history of changes and actions performed on system entities
        </CardDescription>
      </CardHeader>
      <CardContent>
        {showFilters && (
          <div className="mb-6 space-y-4">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4" />
              <h3 className="font-medium">Filters</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Entity Type</label>
                <Select value={filters.entityType} onValueChange={(value) => setFilters({ ...filters, entityType: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="All entity types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All entity types</SelectItem>
                    <SelectItem value="companies">Companies</SelectItem>
                    <SelectItem value="carriers">Carriers</SelectItem>
                    <SelectItem value="work_orders">Work Orders</SelectItem>
                    <SelectItem value="tasks">Tasks</SelectItem>
                    <SelectItem value="contacts">Contacts</SelectItem>
                    <SelectItem value="users">Users</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Entity ID</label>
                <Input
                  placeholder="Enter entity ID"
                  value={filters.entityId}
                  onChange={(e) => setFilters({ ...filters, entityId: e.target.value })}
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">User ID</label>
                <Input
                  placeholder="Enter user ID"
                  value={filters.userId}
                  onChange={(e) => setFilters({ ...filters, userId: e.target.value })}
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleApplyFilters} size="sm">
                Apply Filters
              </Button>
              <Button onClick={handleClearFilters} variant="outline" size="sm">
                Clear Filters
              </Button>
            </div>
          </div>
        )}

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : auditEntries.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No audit trail entries found
          </div>
        ) : (
          <div className="space-y-4">
            {auditEntries.map((entry) => (
              <div
                key={entry.id}
                className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    {actionIcons[entry.action as keyof typeof actionIcons]}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className={actionColors[entry.action as keyof typeof actionColors]}>
                          {entry.action.toUpperCase()}
                        </Badge>
                        <span className="text-sm font-medium">
                          {entry.entityType} (ID: {entry.entityId})
                        </span>
                      </div>
                      
                      {entry.fieldName && (
                        <div className="text-sm text-gray-600 mb-2">
                          Field: <span className="font-medium">{entry.fieldName}</span>
                        </div>
                      )}
                      
                      {(entry.oldValue || entry.newValue) && (
                        <div className="text-sm space-y-1">
                          {entry.oldValue && (
                            <div className="text-red-600">
                              <span className="font-medium">Old:</span> {entry.oldValue}
                            </div>
                          )}
                          {entry.newValue && (
                            <div className="text-green-600">
                              <span className="font-medium">New:</span> {entry.newValue}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="text-right text-sm text-gray-500">
                    <div className="flex items-center gap-1 mb-1">
                      <User className="w-3 h-3" />
                      {entry.userName || entry.userId || 'System'}
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {format(new Date(entry.timestamp), 'MMM d, yyyy h:mm a')}
                    </div>
                    {entry.ipAddress && (
                      <div className="text-xs text-gray-400 mt-1">
                        IP: {entry.ipAddress}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}